import {
  ICollision,
  CollideHandler,
} from '../../actions/collisions/ICollision';
import { InvalidEffect } from '../../actions/effects/InvalidEffect';
import {
  CollideEvent,
  CollideId,
  isOneHitCollider,
  isPowerCollider,
} from '../../defs/collide';
import { despawnBlockEntity } from '../../entities/BlockEntity';
import { GameScene } from '../../GameScene';
import { BigEggBlockEntity } from './BigEggBlockEntity';
import { BigEggBlockEffect } from './BigEggBlockEffect';

export class BigEggBlockCollision implements ICollision {
  private readonly scene: GameScene;
  private readonly entity: BigEggBlockEntity;
  private readonly resolvers: { [key in CollideId]?: CollideHandler } = {
    tap: this.resolveTap,
    block: this.resolveBlock,
    attack: this.resolveAttack,
  };

  constructor(scene: GameScene, entity: BigEggBlockEntity) {
    this.scene = scene;
    this.entity = entity;
  }

  public async resolve(event: CollideEvent): Promise<boolean> {
    return this.resolvers[event.id]?.call(this, event) ?? false;
  }

  private async resolveTap() {
    await new InvalidEffect(this.entity).execute();
    return false;
  }

  private async resolveBlock(event: CollideEvent) {
    if (!this.canAttack(event)) return false;
    this.attack(event);
    return true;
  }

  private async resolveAttack(event: CollideEvent) {
    if (!this.canAttack(event)) return false;
    this.attack(event);
    return true;
  }

  private canAttack(event: CollideEvent) {
    const count = this.entity.c.blockBigEgg.count;
    const maxCount = 7;
    if (count >= maxCount && !isPowerCollider(event.collider?.type)) {
      return false;
    }
    return true;
  }

  private async attack(event: CollideEvent) {
    const entity = this.entity;
    const component = entity.c.blockBigEgg;
    const effect = new BigEggBlockEffect(this.scene);

    // If the collider is a one hit collider, destroy in a single attack
    if (isOneHitCollider(event.collider?.type)) {
      component.count = 1;
    }

    // Play particles on first hit to open the egg or on last hit
    if (component.count <= 1 || component.count >= 7) {
      effect.animateBreak(entity, 'strong');
      effect.animateShake(entity, 'strong');
    }

    if (component.count >= 1 && component.count <= 6) {
      effect.animateBreak(entity, 'soft');
      effect.animateShake(entity, 'soft');
      effect.animateChickFly(entity);

      // Last goal will be triggered by the block despawn
      if (component.count > 1) {
        this.scene.sessionSystem.triggerGoal('bigEgg', this.entity);
      }
    }

    if (component.count <= 1) {
      despawnBlockEntity(this.scene, entity);
    } else {
      component.count -= 1;
      this.scene?.events.publish({ id: 'poke' });
    }
  }
}
